import App from './App.vue'
import i18n from './i18n'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { router } from './router'
import ui from '@nuxt/ui/vue-plugin'
import { addCollection } from '@iconify/vue'
import { icons as lucideIcons } from '@iconify-json/lucide'
import { installKeyboardAssist } from '@/utilities/keyboardAssist'
import { maskito } from '@maskito/vue';

import { diContainer, AppStoreType, ModelsStoreType, ICardTerminal, IErrorReporter, IHtmlGen, II18n, ISettings, AppService, brandTheme } from 'app-core';
import { useAppStore } from '@/stores/useAppStore'
import { name, version, certifiedVersion } from '../package.json';
import ErrorReporter from '@/wrappers/ErrorReporter'
import { Receipt, useModelsStore } from 'app-components';
import { usePwa } from '@/composables/usePwa'
import { createHtmlGen } from 'app-components'
import { BRAND_VAR_NAMES, deriveBrandVars } from 'app-theme'

import VueVirtualScroller from 'vue-virtual-scroller';
import VueQrcode from '@chenfengyuan/vue-qrcode';

/* Virtual scroller */
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

/* App components */
import 'app-components/style.css';
import 'app-components/print.css';

/* Tailwind 4 + Nuxt UI + app theming (the single theming home) */
import './assets/css/main.css';

// Offline icon resolution — register the collection at bootstrap so UIcon never
// hits the Iconify network API (mandatory in the Capacitor webview).
addCollection(lucideIcons)

// Focused-input-above-keyboard nudge.
installKeyboardAssist()

function processUrl(): { tntoken?: string, token?: string, url?: string } {
  const currentUrl = new URL(window.location.href);
  const tntoken = currentUrl.searchParams.get('tntoken') || undefined;
  const token = currentUrl.searchParams.get('token') || undefined;

  if (tntoken || token) {
    currentUrl.searchParams.delete('tntoken');
    currentUrl.searchParams.delete('token');
    return { tntoken, token, url: currentUrl.pathname + currentUrl.search }
  }

  return { tntoken, token }
}

async function initializeApp() {
  const pinia = createPinia()

  const app = createApp(App)
    .use(ui)
    .use(pinia)
    .use(i18n)
    // The library's default cap (1000) throws "Rendered items limit
    // reached" on transient resize-recompute overshoots, not real catalog
    // sizes — no grid here renders anywhere near it (Sentry KONTY-WEB-4Q).
    .use(VueVirtualScroller, { itemsLimit: 5000 })
    .directive('maskito', maskito)

  app.component(String(VueQrcode.name), VueQrcode)

  ErrorReporter.setUpErrorReporting(app)

  const appStore = useAppStore()
  const modelsStore = useModelsStore()

  const settings: ISettings = {
    appName: name,
    version,
    certifiedVersion,
    env: JSON.parse(JSON.stringify(import.meta.env).replace(/VITE_/g, '')),
    electron: window.electron,
    onUnauthorized: (isTenantUnauthorized: boolean) => {
      if (isTenantUnauthorized) router.push({ name: 'tenantLogin' })
      else router.push({ name: 'userLogin' })
    },
    onLocaleChange: (locale: string) => {}
  }

  diContainer.register<AppStoreType>('store', appStore)
  diContainer.register<ModelsStoreType>('modelsStore', modelsStore)
  diContainer.register<II18n>('i18n', i18n.global)
  diContainer.register<ISettings>('settings', settings)
  diContainer.register<IErrorReporter>('errorReporter', ErrorReporter)
  diContainer.register<IHtmlGen>('htmlGen', createHtmlGen({ i18n }))
  diContainer.register('orderRenderComponent', Receipt)

  // E2E shells only: the card terminal is an HTTP double the Playwright suite
  // scripts. The URL is baked by test:e2e:electron:build alone. VITE_ENV is a
  // literal in every build, so a production build folds this branch to dead
  // code and ships neither the registration nor the chunk. (import.meta.env.DEV
  // would not do: `vite build --mode development` still builds with NODE_ENV
  // production, so DEV is false in the e2e renderer too.)
  if (import.meta.env.VITE_ENV !== 'production' && import.meta.env.VITE_E2E_CARD_TERMINAL_URL) {
    const { FakeCardTerminal } = await import('./e2e/FakeCardTerminal')
    diContainer.register<ICardTerminal>('cardTerminal', new FakeCardTerminal(import.meta.env.VITE_E2E_CARD_TERMINAL_URL))
  }

  // Brand theme adapter — reseller co-labeling lands as the
  // --brand-* inline var family on <html>; theme.css/main.css consume them
  // as var(--brand-*, <stock>) fallbacks, so the theme blocks keep owning
  // light vs dark and clearing the vars restores the stock look exactly.
  brandTheme.registerAdapter(brand => {
    const root = document.documentElement
    const vars = brand ? deriveBrandVars(brand.colors) : null
    for (const name of BRAND_VAR_NAMES) {
      if (vars) root.style.setProperty(name, vars[name])
      else root.style.removeProperty(name)
    }
  })

  AppService.setUpDevelopmentMode()

  const { setupPWAAutoUpdate } = usePwa()
  const periodMinutes = import.meta.env.VITE_ENV === 'production' ? 60 : 5
  setupPWAAutoUpdate(periodMinutes * 60 * 1000)

  // Initialize the app
  const { tntoken, token, url } = processUrl()
  await AppService.initializeApp({ tntoken, token })

  brandTheme.applyFromLocale()

  return { app, url }
}

async function startApp() {
  const { app, url } = await initializeApp()

  app.use(router)
  if (url) router.replace(url)

  app.mount('#app')
}

// public/webview-guard.js runs before this bundle; on a WebView below the
// Tailwind v4 renderer floor it has already replaced the page with the
// update notice, and booting the app on top of it would only add noise.
if (!(window as Window & { __kontyRendererUnsupported?: boolean }).__kontyRendererUnsupported) {
  startApp()
}
